import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";
export type ThemePref = Theme | "system";
const KEY = "eesweb-theme";
const SYNC_EVENT = "eesweb-theme-change";

// Runs in <head> before first paint so a dark-mode user never sees a white flash.
export const THEME_BOOT_SCRIPT = `(function(){try{var t=localStorage.getItem("${KEY}");if(!t)t=matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light";if(t==="dark")document.documentElement.classList.add("dark");document.documentElement.style.colorScheme=t;}catch(e){}})();`;

function current(): Theme {
  return typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light";
}
function systemTheme(): Theme {
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function storedPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

function apply(t: Theme) {
  const root = document.documentElement;
  root.classList.add("theme-switching");
  root.classList.toggle("dark", t === "dark");
  root.style.colorScheme = t;
  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove("theme-switching")));
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>("light");
  const [pref, setPrefState] = useState<ThemePref>("system");

  useEffect(() => {
    setThemeState(current());
    setPrefState(storedPref());
    // Follow the device live while the user hasn't picked a theme.
    const mq = matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (storedPref() !== "system") return;
      const t = systemTheme();
      apply(t); setThemeState(t);
    };
    mq.addEventListener("change", onChange);
    // Keep every mounted toggle (header button, Settings picker) in sync.
    const onSync = () => { setThemeState(current()); setPrefState(storedPref()); };
    window.addEventListener(SYNC_EVENT, onSync);
    return () => { mq.removeEventListener("change", onChange); window.removeEventListener(SYNC_EVENT, onSync); };
  }, []);

  const setPreference = useCallback((p: ThemePref) => {
    try {
      if (p === "system") localStorage.removeItem(KEY); else localStorage.setItem(KEY, p);
    } catch { /* private mode: theme still applies for this visit */ }
    const t = p === "system" ? systemTheme() : p;
    apply(t);
    setThemeState(t);
    setPrefState(p);
    window.dispatchEvent(new Event(SYNC_EVENT));
  }, []);

  return {
    theme,
    preference: pref,
    setPreference,
    setTheme: (t: Theme) => setPreference(t),
    toggle: () => setPreference(current() === "dark" ? "light" : "dark"),
  };
}
